import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
// Config
import { IMAGE_BASE_URL, BACKDROP_SIZE, DETAIL_POSTER_SIZE } from '../../config';
// Helpers
import { getYear, calcTime, initials } from '../../helpers';
// Components
import RatingRing from '../RatingRing';
// Styles
import { Wrapper, Content, Poster, Text, Crumb } from './MovieHero.styles';

const People = ({ label, people }) =>
  people.length ? (
    <div>
      <dt>
        {label}
        {people.length > 1 ? 's' : ''}
      </dt>
      <dd>{people.map(p => p.name).join(', ')}</dd>
    </div>
  ) : null;

const MovieHero = ({ movie }) => {
  const year = getYear(movie.release_date);

  return (
    <Wrapper
      $backdrop={
        movie.backdrop_path
          ? `${IMAGE_BASE_URL}${BACKDROP_SIZE}${movie.backdrop_path}`
          : null
      }
    >
      <Content>
        <Crumb>
          <Link to='/'>
            <span aria-hidden='true'>←</span> All movies
          </Link>
          <span aria-hidden='true'>/</span>
          <span className='current'>{movie.title}</span>
        </Crumb>

        <div className='layout'>
          <Poster>
            {movie.poster_path ? (
              <img
                src={`${IMAGE_BASE_URL}${DETAIL_POSTER_SIZE}${movie.poster_path}`}
                alt={`${movie.title} poster`}
              />
            ) : (
              <div className='fallback'>{initials(movie.title)}</div>
            )}
          </Poster>

          <Text>
            <div className='kicker'>
              {year && <span>{year}</span>}
              {movie.runtime ? <span>{calcTime(movie.runtime)}</span> : null}
              {movie.original_language && (
                <span>{movie.original_language.toUpperCase()}</span>
              )}
              {movie.status && movie.status !== 'Released' && (
                <span className='status'>{movie.status}</span>
              )}
            </div>
            <h1>{movie.title}</h1>
            {movie.original_title && movie.original_title !== movie.title && (
              <p className='original'>{movie.original_title}</p>
            )}
            {movie.tagline && <p className='tagline'>“{movie.tagline}”</p>}

            {movie.genres && movie.genres.length > 0 && (
              <ul className='genres'>
                {movie.genres.map(genre => (
                  <li key={genre.id}>{genre.name}</li>
                ))}
              </ul>
            )}

            <div className='overview'>
              <h2>Synopsis</h2>
              <p>{movie.overview || 'No synopsis is available for this title yet.'}</p>
            </div>

            <div className='bottom'>
              <RatingRing rating={movie.vote_average} votes={movie.vote_count} />
              <dl>
                <People label='Director' people={movie.directors || []} />
                <People label='Writer' people={movie.writers || []} />
              </dl>
            </div>

            <div className='links'>
              <a
                href={`https://www.themoviedb.org/movie/${movie.id}`}
                target='_blank'
                rel='noreferrer'
              >
                View on TMDB ↗
              </a>
              {movie.imdb_id && (
                <a
                  href={`https://www.imdb.com/title/${movie.imdb_id}/`}
                  target='_blank'
                  rel='noreferrer'
                >
                  IMDb ↗
                </a>
              )}
              {movie.homepage && (
                <a href={movie.homepage} target='_blank' rel='noreferrer'>
                  Official site ↗
                </a>
              )}
            </div>
          </Text>
        </div>
      </Content>
    </Wrapper>
  );
};

MovieHero.propTypes = {
  movie: PropTypes.object.isRequired
};

export default MovieHero;
